/**
 * Pricing constants, re-exported from `DEFAULT_PRICING_CONFIG_BODY`.
 *
 * Phase 1 architecture: `pricing-config/defaults.ts` is the single literal
 * source of truth for every pricing number. This file is a back-compat
 * shim — engine helpers (`getCPC`, `getSPRetailPrintRate`, …) keep
 * importing the named constants they always have, but the values now flow
 * from one source so drift is structurally impossible.
 *
 * Per CLAUDE.md § 1.1, changes to these values require owner sign-off and
 * will break golden fixtures — edit `defaults.ts`, not this file.
 */
import { DEFAULT_PRICING_CONFIG_BODY } from './pricing-config/defaults.js';
const D = DEFAULT_PRICING_CONFIG_BODY;
// ─────────────────────────────────────────────────────────────────
// Universal
// ─────────────────────────────────────────────────────────────────
export const SIZE_UPCHARGE = D.universal.sizeUpcharge;
// ─────────────────────────────────────────────────────────────────
// Screen Print
// ─────────────────────────────────────────────────────────────────
export const SP_MIN_QTY = D.screenPrint.minQty;
export const SP_CPC_TIERS = D.screenPrint.cpcTiers;
export const SP_EXTRA_COLOR_UPCHARGE = D.screenPrint.extraColorUpcharge;
export const SP_LOCATION_UPCHARGE = D.screenPrint.locationUpcharge;
export const SP_INTERNAL_LOCATION_COST = D.screenPrint.internalLocationCost;
export const SP_SETUP_TIERS = D.screenPrint.setupTiers;
export const SP_WHOLESALE_PRINT_TIERS = D.screenPrint.wholesalePrintTiers;
export const SP_RETAIL_PRINT_TIERS = D.screenPrint.retailPrintTiers;
export const SP_FLOOR_TIERS = D.screenPrint.floorTiers;
export const SP_DARK_UNDERBASE = D.screenPrint.darkUnderbase;
// ─────────────────────────────────────────────────────────────────
// DTF Transfer
// ─────────────────────────────────────────────────────────────────
export const DTF_SHEET_WIDTH_IN = D.dtfTransfer.sheetWidthIn;
export const DTF_INTERNAL_COST_PER_SQIN = D.dtfTransfer.internalCostPerSqIn;
export const DTF_BASE_RATE_TIERS = D.dtfTransfer.baseRateTiers;
export const DTF_B2B_MULTIPLIER = D.dtfTransfer.b2bMultiplier;
export const DTF_B2B_MIN_RATE = D.dtfTransfer.b2bMinRate;
export const DTF_ETSY_DEFAULT_RATE = D.dtfTransfer.etsyDefaultRate;
export const DTF_ETSY_MULTIPLIER = D.dtfTransfer.etsyMultiplier;
export const DTF_FLOOR = D.dtfTransfer.floor;
// ─────────────────────────────────────────────────────────────────
// DTG
// ─────────────────────────────────────────────────────────────────
export const DTG_HARD_MAX_QTY = D.dtg.hardMaxQty;
export const DTG_SOFT_MAX_QTY = D.dtg.softMaxQty;
export const DTG_INTERNAL_COST = D.dtg.internalCost;
export const DTG_PRICE_TIERS = D.dtg.priceTiers;
export const DTG_MARGIN_WARN_FLOOR_PCT = D.dtg.marginWarnFloorPct;
// ─────────────────────────────────────────────────────────────────
// Embroidery
// ─────────────────────────────────────────────────────────────────
export const EMB_MIN_QTY = D.embroidery.minQty;
export const EMB_BASE_TIERS = D.embroidery.baseTiers;
export const EMB_COMPLEXITY_MULTIPLIER = D.embroidery.complexityMultiplier;
export const EMB_ADDITIONAL_LOCATION_TIERS = D.embroidery.additionalLocationTiers;
export const EMB_NAME_TIERS = D.embroidery.nameTiers;
export const EMB_NUMBER_MULTIPLIER = D.embroidery.numberMultiplier;
export const EMB_STITCH_TIERS = D.embroidery.stitchTiers;
export const EMB_COST_DEFAULTS = D.embroidery.costDefaults;
export const EMB_DIGI_COSTS = D.embroidery.digiCosts;
export const EMB_DIGI_CUSTOMER_MARKUP = D.embroidery.digiCustomerMarkup;
// ─────────────────────────────────────────────────────────────────
// DTF Press & Press
// ─────────────────────────────────────────────────────────────────
export const DTFPP_TRANSFER_COST_PER_SQIN = D.dtfPressAndPress.transferCostPerSqIn;
export const DTFPP_LABOR_PER_LOCATION_DEFAULT = D.dtfPressAndPress.laborPerLocationDefault;
export const DTFPP_MIN_SELL_BEFORE_LABOR_BLANK = D.dtfPressAndPress.minSellBeforeLaborBlank;
export const DTFPP_MARGIN_TIERS = D.dtfPressAndPress.marginTiers;
export const DTFPP_MARGIN_WARN_FLOOR_PCT = D.dtfPressAndPress.marginWarnFloorPct;
export const DTFPP_RATE_WARN = D.dtfPressAndPress.rateWarn;
// ─────────────────────────────────────────────────────────────────
// Routing
// ─────────────────────────────────────────────────────────────────
export const ROUTE_THRESHOLDS = D.routing.thresholds;
// ─────────────────────────────────────────────────────────────────
// Rush multipliers (locked by golden fixtures + owner sign-off)
// ─────────────────────────────────────────────────────────────────
/**
 * Rush surcharge applied AFTER discount to per-unit sell. Floor enforcement
 * still runs against the pre-rush sell — rush is sell-side markup, never
 * a way to climb out of a below-floor quote.
 *
 * Owner-confirmed values (2026-04-25):
 *   - `rush`     +25% — confirmed for standard rush turnaround.
 *   - `hot_rush` +50% — provisional industry default; pending explicit
 *                       owner sign-off + golden fixture lock.
 */
export const RUSH_MULTIPLIERS = D.screenPrint.rushMultipliers;
//# sourceMappingURL=constants.js.map